/** Anatomical weapon reach, safe held poses and the authored Fable adapter. */
import type { EquipmentWeaponId } from '../../shared/equipment';
import type { Skeleton, V3 } from './rig';
import { evaluate, qAxisAngle, qEuler, qMul, type Pose, type Quat } from './pose';
import { weaponGrip } from './weapons';
import {retargetFable} from './fableRetarget';

import {weaponMotion,isSuperAction,type ArmedAction} from '../../shared/weapon-motion';
export {weaponMotion} from '../../shared/weapon-motion';
const clamp=(v:number)=>Math.max(0,Math.min(1,v)),smooth=(v:number)=>{const x=clamp(v);return x*x*(3-2*x);};
type Key=readonly [number,number];
export interface WeaponHead { center:V3; radius:number }
function track(t:number,keys:readonly Key[]):number {
  if(t<=keys[0][0])return keys[0][1];
  for(let i=1;i<keys.length;i++)if(t<=keys[i][0]){const [at,a]=keys[i-1],[bt,b]=keys[i];return a+(b-a)*smooth((t-at)/(bt-at));}
  return keys.at(-1)![1];
}
const dot=(a:number[],b:number[])=>a.reduce((v,x,i)=>v+x*b[i],0);
const norm=(a:number[]):V3=>{const n=Math.hypot(...a)||1;return a.map(v=>v/n) as V3;};
const cross=(a:number[],b:number[]):V3=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
const inverse=([x,y,z,w]:Quat):Quat=>[-x,-y,-z,w];
const rotate=(q:Quat,v:number[]):V3=>qMul(qMul(q,[...v,0] as Quat),inverse(q)).slice(0,3) as V3;
function aim(from:number[],to:number[]):Quat {
  const f=norm(from),t=norm(to),d=dot(f,t);
  if(d<-.99999){const axis=norm(cross(f,Math.abs(f[0])<.8?[1,0,0]:[0,1,0]));return qAxisAngle(...axis,Math.PI);}
  const q=[...cross(f,t),1+d],length=Math.hypot(...q)||1;return q.map(v=>v/length) as Quat;
}
/** Two-bone reach followed by a rigid weapon orientation. The forearm is bent
 * away from the torso and all targets remain within the actual morphed reach. */
function arm(sk:Skeleton,pose:Pose,side:'R'|'L',weapon:EquipmentWeaponId,wrist:V3,shaft:V3,chestLocal=false,sourcePole?:V3):{wrist:V3;rotation:Quat;shaft:V3} {
  const upper=sk.byName.get('upperArm'+side)!,fore=sk.byName.get('foreArm'+side)!,hand=sk.byName.get('hand'+side)!,chest=sk.byName.get('chest')!;
  const direction=(i:number)=>sk.bones[i].tail.map((v,k)=>v-sk.bones[i].head[k]) as V3;
  const ev=evaluate(sk,pose),frame=ev.world[chest],p=ev.joints[upper].map((v,i)=>v-frame[9+i]);
  const local=(v:V3):V3=>[0,1,2].map(i=>frame[i*3]*v[0]+frame[i*3+1]*v[1]+frame[i*3+2]*v[2]) as V3;
  // The torso may coil underneath an attack; its twist must not rotate the
  // contact target away from the opponent (especially the reverse flurry cut).
  if(!chestLocal){wrist=local(wrist);shaft=local(shaft);if(sourcePole)sourcePole=local(sourcePole);}
  const shoulder=[0,1,2].map(i=>frame[i*3]*p[0]+frame[i*3+1]*p[1]+frame[i*3+2]*p[2]) as V3;
  const l1=Math.hypot(...direction(upper))*pose.bones[upper].length,l2=Math.hypot(...direction(fore))*pose.bones[fore].length;
  const sign=side==='R'?-1:1;
  let pole=sourcePole??[sign*.85,-.65,1.3];
  if(sourcePole){
    const pelvis=sk.byName.get('pelvis')!,torso=Math.max(sk.bones[pelvis].radius*pose.bones[pelvis].thick,sk.bones[chest].radius*pose.bones[chest].thick);
    const weight=.9*smooth((torso/(l1+l2)-.36)/.24),safe=[sign*.85,-.65,1.3];
    pole=norm(pole.map((v,i)=>v*(1-weight)+safe[i]*weight));
    // Project an inward wrist toward its own flank before solving. A radial
    // boundary is continuous as the hand enters/leaves the torso silhouette;
    // it avoids switching between two equally near elbow routes round a belly.
    const r=torso+Math.max(sk.bones[fore].radius,sk.bones[hand].radius)+.018;
    const cap=Math.max(0,(wrist[1]-shoulder[1]+.04)/.12),width=r*Math.sqrt(Math.max(0,1-cap*cap));
    if(wrist[1]>-.3&&cap<1&&Math.abs(wrist[2])<width){
      const x=Math.sqrt(Math.max(0,width*width-wrist[2]*wrist[2])),a=sign*wrist[0],epsilon=.012;
      wrist=[sign*(.5*(a+x+Math.sqrt((a-x)**2+epsilon*epsilon))),wrist[1],wrist[2]];
    }
  }
  const delta=wrist.map((v,i)=>v-shoulder[i]),axis=norm(delta),reach=Math.min((l1+l2)*.965,Math.max(Math.abs(l1-l2)+.001,Math.hypot(...delta)));
  const along=(l1*l1-l2*l2+reach*reach)/(2*reach),bend=Math.sqrt(Math.max(0,l1*l1-along*along));
  const out=norm(pole.map((v,i)=>v-axis[i]*dot(pole,axis)));
  const elbow=shoulder.map((v,i)=>v+axis[i]*along+out[i]*bend),goal=shoulder.map((v,i)=>v+axis[i]*reach);
  const u=aim(direction(upper),elbow.map((v,i)=>v-shoulder[i]));
  const f=aim(direction(fore),rotate(inverse(u),goal.map((v,i)=>v-elbow[i])));
  pose.bones[upper].rot=u;pose.bones[fore].rot=f;
  const grip=weaponGrip(sk,side,weapon),target=norm(shaft),point=aim(grip.across,target);
  const fingers=rotate(point,grip.along),desired=Math.abs(target[1])>.85?[sign,0,0]:[0,-1,0];
  const plane=norm(desired.map((v,i)=>v-target[i]*dot(desired,target)));
  const twist=Math.atan2(dot(target,cross(fingers,plane)),dot(fingers,plane));
  const rotation=qMul(qAxisAngle(...target,twist),point);
  pose.bones[hand].rot=qMul(inverse(qMul(u,f)),rotation);
  return {wrist:goal as V3,rotation,shaft:target};
}

/** Called after proportion scales and a neutral stance have been applied.
 * Returns deliberate airborne height; grounding runs before this offset. */
export function applyArmedMotion(sk:Skeleton,pose:Pose,time:number,weapon:EquipmentWeaponId,action:ArmedAction|'idle'|'walk'|'guard'|'prayer'|'greet'|'victory',head?:WeaponHead):number {
  if(action==='strike'||isSuperAction(action))return retargetFable(sk,pose,time,weapon,action,(side,wrist,shaft,pole)=>{arm(sk,pose,side,weapon,wrist,shaft,false,pole);});
  const bi=(name:string)=>sk.byName.get(name)!,set=(name:string,x:number,y:number,z:number)=>{const i=bi(name);pose.bones[i].rot=qMul(pose.bones[i].rot,qEuler(x,y,z));};
  const chest=bi('chest'),pelvis=bi('pelvis');
  const torso=Math.max(sk.bones[chest].radius*pose.bones[chest].thick,sk.bones[pelvis].radius*pose.bones[pelvis].thick);
  const reach=['upperArmR','foreArmR'].reduce((sum,name)=>{const i=bi(name),b=sk.bones[i];return sum+Math.hypot(...b.tail.map((v,k)=>v-b.head[k]))*pose.bones[i].length;},0);
  const shoulderY=sk.bones[bi('upperArmR')].head[1]-sk.bones[chest].head[1];
  const ready:V3=[-torso-.035,shoulderY-reach*.58,torso*.75];
  let right:V3=[...ready],left:V3=[torso+.035,shoulderY-reach*.58,torso*.7],shaft:V3=[-.15,.92,.22],leftShaft:V3=[.15,.92,.22];
  let hover=0,facing=0;
  if(action==='guard') {
    const brace=track(time,[[0,0],[.18,1],[.36,1],[.46,.8],[.72,1]]),recoil=track(time,[[0,0],[.36,0],[.43,1],[.58,0],[.72,0]]);
    set('pelvis',-.08*recoil,-.16*brace,0);set('chest',-.14*recoil,-.2*brace,-.035*brace);
    for(const side of ['R','L']){set('thigh'+side,-.22*brace,0,side==='R'?-.05:.05);set('shin'+side,.4*brace,0,0);}
    right=[weapon==='hammer'?-torso-.045:-torso*.62,shoulderY-.075+.04*brace,torso+reach*(.25+.05*brace-.1*recoil)];shaft=weapon==='hammer'?[-.78,.56,.15]:[.68,.7,.12];
    left=[torso*.7,shoulderY-.045,torso+reach*(.25-.08*recoil)];leftShaft=[-.68,.7,.12];
  }else if(action==='victory') {
    const salute=smooth(time/.6);
    right=[-torso-.065,ready[1]+reach*1.08*salute,torso*.55];shaft=[-.38,.92,.08];
    left=[torso+.065,ready[1]+reach*.76*salute,torso*.55];leftShaft=[.38,.92,.08];
  }else if(action==='prayer') {
    // The hand-held prop is lowered beside the torso while the other hand
    // makes the devotional gesture; no blade is driven through joined palms.
    right=[-torso-.045,shoulderY-reach*.65,torso*.35];shaft=[-.12,1,.06];
  }
  const spin=qEuler(0,facing,0);
  const rightArm=arm(sk,pose,'R',weapon,rotate(spin,right),rotate(spin,shaft));
  if(weapon==='daggers')arm(sk,pose,'L',weapon,rotate(spin,left),rotate(spin,leftShaft));
  else if((weapon==='hammer'||weapon==='staff')&&['strike','starfall'].includes(action)) {
    const grip=weaponGrip(sk,'R',weapon),head=sk.bones[grip.bone].head;
    const offset=rotate(rightArm.rotation,grip.center.map((v,i)=>v-head[i]));
    const axis=rightArm.shaft,center=rightArm.wrist.map((v,i)=>v+offset[i]-axis[i]*.065) as V3;
    // The support hand follows the resolved grip, never an unreachable artistic
    // target that the primary arm had to clamp on a short-armed body.
    const support=arm(sk,pose,'L',weapon,center,axis,true),leftGrip=weaponGrip(sk,'L',weapon),lh=sk.bones[leftGrip.bone].head;
    const leftOffset=rotate(support.rotation,leftGrip.center.map((v,i)=>v-lh[i]));
    arm(sk,pose,'L',weapon,center.map((v,i)=>v-leftOffset[i]) as V3,axis,true);
  }
  if(!['strike','starfall','thousandCuts'].includes(action)) {
    // A short arm or a large grafted head changes the safe blade angle. Solve
    // a tangent from the actual posed grip to the actual head envelope, so a
    // guard never raises a sword through the nose on another body proportion.
    const hi=bi('head'),shape=head??{center:sk.bones[hi].head,radius:sk.bones[hi].radius};
    const sides:('R'|'L')[]=weapon==='daggers'?['R','L']:['R'];
    for(const side of sides)for(let pass=0;pass<2;pass++) {
      const ev=evaluate(sk,pose),grip=weaponGrip(sk,side,weapon);
      const point=(bone:number,p:V3):V3=>{const m=ev.skin,o=bone*16;return [0,1,2].map(i=>m[o+i]*p[0]+m[o+4+i]*p[1]+m[o+8+i]*p[2]+m[o+12+i]) as V3;};
      const center=point(hi,shape.center),origin=point(grip.bone,grip.center),tip=point(grip.bone,grip.tip);
      const delta=center.map((v,i)=>v-origin[i]),distance=Math.hypot(...delta),axis=norm(tip.map((v,i)=>v-origin[i]));
      const headRadius=shape.radius*pose.bones[hi].thick,radius=headRadius+.035;
      const length=Math.hypot(...tip.map((v,i)=>v-origin[i])),along=Math.max(0,Math.min(length,dot(delta,axis)));
      const tipRadius=headRadius+(weapon==='hammer'?.15:weapon==='staff'?.065:.045);
      const shaftHit=Math.hypot(...delta.map((v,i)=>v-axis[i]*along))<radius;
      const tipHit=Math.hypot(...tip.map((v,i)=>v-center[i]))<tipRadius;
      if(!shaftHit&&!tipHit)break;
      const chestOrigin=ev.joints[chest],wrist=ev.joints[grip.bone].map((v,i)=>v-chestOrigin[i]) as V3;
      const sidePole:V3=[side==='R'?-1:1,-.15,.5],h=norm(delta);
      if(distance<radius+.005){
        const away=norm([sidePole[0]*.6,-.8,.5]),push=radius-distance+.035;
        arm(sk,pose,side,weapon,wrist.map((v,i)=>v+away[i]*push) as V3,axis);continue;
      }
      // Only the far hammer head is broad; treating the entire handle as a
      // thick capsule makes a short arm's valid grip impossible to resolve.
      const capAngle=Math.acos(Math.max(-1,Math.min(1,(distance*distance+length*length-tipRadius*tipRadius)/(2*distance*length))));
      const tangent=norm(sidePole.map((v,i)=>v-h[i]*dot(sidePole,h))),angle=Math.max(shaftHit?Math.asin(Math.min(.999,radius/distance)):0,tipHit?capAngle:0)+.045;
      const safe=h.map((v,i)=>v*Math.cos(angle)+tangent[i]*Math.sin(angle)) as V3;
      arm(sk,pose,side,weapon,wrist,safe);
    }
  }
  // Counter-rotate planted feet against pelvis/thigh/shin articulation. The
  // ankle stays level instead of tilting toes through the plinth on a crouch.
  if(['strike','starfall','thousandCuts'].includes(action))for(const side of ['R','L']) {
    if(hover>.015){pose.bones[bi('foot'+side)].rot=qEuler(-.12,0,0);continue;}
    const sum=qMul(qMul(pose.bones[pelvis].rot,pose.bones[bi('thigh'+side)].rot),pose.bones[bi('shin'+side)].rot);
    pose.bones[bi('foot'+side)].rot=inverse(sum);
  }
  return hover;
}
